import type { ErpSyncAuditResponse } from '../services/api';

type AuditMapping = ErpSyncAuditResponse['mappings'][number];
type AuditLive = ErpSyncAuditResponse['live'][number];

/**
 * Pair the live Shopify listing with the mapping it actually belongs to. An org
 * with more than one integration would otherwise compare one store's live
 * listing against another store's last push and report drift that isn't there.
 */
export function pickMappedLiveListing(data?: ErpSyncAuditResponse | null): {
  mapping: AuditMapping | undefined;
  live: AuditLive | undefined;
} {
  const mappings = data?.mappings ?? [];
  const liveEntries = data?.live ?? [];
  const mapping =
    mappings.find((m) => m.lastPushed && liveEntries.some((l) => l.integrationId === m.integrationId)) ??
    mappings.find((m) => m.lastPushed) ??
    mappings[0];
  const live =
    liveEntries.find((l) => l.integrationId === mapping?.integrationId) ??
    // Single mapping, single listing: pair them even if the ids don't line up.
    (liveEntries.length === 1 && mappings.length === 1 ? liveEntries[0] : undefined);
  return { mapping, live };
}

/**
 * The price the store's mapped Shopify listing is charging right now, read
 * from the Shopify Admin API. This, not leftover catalog retail, is what the
 * store's customers see. Null when there is no mapped listing, the read
 * failed, or the live price belongs to a different variant of a shared listing.
 */
export function liveStorefrontPrice(data?: ErpSyncAuditResponse | null): number | null {
  const { live } = pickMappedLiveListing(data);
  if (!live || live.error || live.skuMatched === false) return null;
  const price = Number(live.price);
  return Number.isFinite(price) && price > 0 ? price : null;
}
